import { fetchJsonWithAuth, apiGetWithAuth } from './authApi';
import { AUTH_URL, CANDIDATES_URL, JOBS_URL, SUBMISSIONS_URL } from './apiConfig';

function getTenantContext() {
  return {
    tenantId: localStorage.getItem('tenantId') || '',
    businessId: localStorage.getItem('businessId') || '',
    businessUnitId: localStorage.getItem('businessUnitId') || '',
  };
}

function getUserId() {
  return Number(localStorage.getItem('userId')) || 0;
}

const unwrap = (json) => json?.data ?? json;

// Assigned-user rows arrive from several Jobs endpoints in different shapes.
// Some carry a recruiterName, some only first_name, and older records may carry
// nothing but the numeric legacy user id. Resolve that id through Auth so both
// Self Assign and Assign to Others show a person's name rather than an opaque
// number. Requests are cached because both popups can ask for the same people.
const assignedUserNameCache = new Map();

function assignedUserId(user) {
  return user?.recruiterId
    ?? user?.legacyUserId
    ?? user?.user_id
    ?? user?.userId
    ?? user?.USER_ID
    ?? user?.id
    ?? user?._id;
}

function assignedUserDisplayName(user) {
  if (!user || typeof user !== 'object') return '';
  const firstName = user.first_name ?? user.firstName ?? user.FIRST_NAME;
  const lastName = user.last_name ?? user.lastName ?? user.LAST_NAME;
  return [firstName, lastName].filter(Boolean).join(' ').trim()
    || user.recruiterName
    || user.name
    || user.user_name
    || user.userName
    || user.USER_NAME
    || user.email
    || '';
}

async function fetchAssignedUserName(userId) {
  const key = String(userId ?? '').trim();
  if (!key) return '';
  if (!assignedUserNameCache.has(key)) {
    assignedUserNameCache.set(key,
      fetchJsonWithAuth(AUTH_URL, `/get-detailed-view?userId=${encodeURIComponent(key)}`)
        .then((json) => assignedUserDisplayName(json?.data ?? json ?? null))
        .catch(() => ''),
    );
  }
  return assignedUserNameCache.get(key);
}

// ── The user directory ───────────────────────────────────────────────────────
// One fetch of everyone, indexed by EVERY id shape a job row might carry.
//
// The per-id lookup above answers only "what is this person called". Assigned
// people are also shown with their ROLE and REPORTING MANAGER (the Self Assign
// "Team Members Allocated" table), and nothing was ever populating those — so
// both columns rendered "-" on every row, for every job. The directory carries
// all three, so one request answers the whole table instead of N requests
// answering a third of it.
//
// Cached for the session: the same people appear across Self Assign, Assign to
// Others, and repeated opens of both.
let userDirectoryPromise = null;

function indexUserDirectory(rows) {
  const byId = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const entry = {
      name: assignedUserDisplayName(row),
      role_name: row?.roleName ?? row?.role_name ?? '',
      reporting_name: row?.reportingName ?? row?.reporting_name ?? '',
    };
    // A job stores whichever id its era used, so index them all rather than
    // guessing which one this deployment writes.
    [row?.userId, row?.user_id, row?.legacyUserId, row?.id, row?._id].forEach((id) => {
      const key = String(id ?? '').trim();
      if (key) byId.set(key, entry);
    });
  });
  return byId;
}

function fetchUserDirectory() {
  if (!userDirectoryPromise) {
    userDirectoryPromise = fetchJsonWithAuth(AUTH_URL, '/get-all-users?limit=500&offset=0')
      .then((json) => {
        const payload = json?.data ?? json ?? {};
        return indexUserDirectory(payload.users ?? payload ?? []);
      })
      // Best effort: a directory that will not load must not stop the popup —
      // rows fall back to whatever the job row itself carries.
      .catch(() => new Map());
  }
  return userDirectoryPromise;
}

async function enrichAssignedUser(user) {
  const id = assignedUserId(user);
  const directory = await fetchUserDirectory();
  const entry = directory.get(String(id ?? '').trim()) ?? {};
  // Precedence matters and is easy to get backwards: a CURRENT name must beat
  // the one the job stored, because a person who has since been renamed should
  // not keep appearing under their old name. So it is bulk directory → per-id
  // lookup → whatever the job row carried (deleted user / directory down).
  const name = entry.name || (await fetchAssignedUserName(id)) || assignedUserDisplayName(user);
  return {
    ...user,
    name,
    role_name: entry.role_name || user?.role_name || user?.roleName || '',
    reporting_name: entry.reporting_name || user?.reporting_name || user?.reportingName || '',
  };
}

// ── Change Status ────────────────────────────────────────────────────────────

export async function changeModuleStatus(module, ids, status) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/module-data-update', {
    method: 'PUT',
    body: JSON.stringify({ module, ids, updates: { status } }),
  });
  return unwrap(json);
}

// ── Delete ───────────────────────────────────────────────────────────────────

// One common delete for EVERY module — the same DB-driven gateway that already
// serves add/edit. The gateway is storage-aware: it soft-deletes a mongo-storage
// module in place, or proxies to a proxy-module's downstream delete endpoint
// (jobs/candidates/…) using that module's configured gateway.deletePath — so
// there is no hardcoded per-module routing here anymore. Role gating is handled
// upstream by the action's delete permission (canDelete) before this runs.
export async function deleteModuleRecords(module, ids) {
  const list = Array.isArray(ids) ? ids : [ids];
  const results = await Promise.all(
    list
      .filter(Boolean)
      .map((id) =>
        fetchJsonWithAuth(
          AUTH_URL,
          `/module/delete/${encodeURIComponent(id)}?module=${encodeURIComponent(module)}`,
          { method: 'DELETE' },
        ),
      ),
  );
  return results[results.length - 1] ?? {};
}

// ── Job search (shared by quick-submit and tag-to-job) ───────────────────────

export async function searchJobs(searchQuery) {
  const json = await fetchJsonWithAuth(JOBS_URL, '/joblist', {
    method: 'POST',
    body: JSON.stringify({
      ...getTenantContext(),
      searchQuery,
      userId: getUserId(),
    }),
  });
  return unwrap(json);
}

// ── Quick Submit ─────────────────────────────────────────────────────────────

export async function searchCandidatesForSubmit(jobId, search = '') {
  const json = await fetchJsonWithAuth(CANDIDATES_URL, '/quicksubmit/list', {
    method: 'POST',
    body: JSON.stringify({ jobId, search }),
  });
  return unwrap(json);
}

export async function quickSubmitCandidate({ jobId, candidateId }) {
  const json = await fetchJsonWithAuth(SUBMISSIONS_URL, '/review', {
    method: 'POST',
    body: JSON.stringify({
      ...getTenantContext(),
      jobId,
      candidateId: Array.isArray(candidateId) ? candidateId : [candidateId],
    }),
  });
  return unwrap(json);
}

// ── Add Notes & Share ─────────────────────────────────────────────────────────

export async function getMentionUsers(search = '') {
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  return apiGetWithAuth(JOBS_URL, `/users/any${params}`);
}

// Note: `mentionedUsers` (usernames parsed out of the '@mention' text) is
// intentionally not sent — the backend's NotesMaster.receiver field is a
// single numeric user id, not a list of usernames, so forwarding it always
// fails unmarshaling ("cannot unmarshal string into ... of type int").
export async function addStickyNote({ relatedIds, module, title, targetDate, notes }) {
  return fetchJsonWithAuth(JOBS_URL, '/notes', {
    method: 'POST',
    body: JSON.stringify({
      schemaVersion: 1,
      ...getTenantContext(),
      title,
      notes,
      notesFor: module || 'general',
      // The backend expects an ObjectID or null here, not an empty array —
      // a plain dashboard note (no selected row) has nothing to relate to.
      notesRelatedId: Array.isArray(relatedIds) && relatedIds.length ? relatedIds : null,
      // An empty-string reminder time fails backend time parsing — omit the
      // whole object (like a null target date) instead of sending "".
      reminder: targetDate ? { type: 'single', time: targetDate, interval: '' } : null,
      targetDate: targetDate || null,
    }),
  });
}

// PUT /notes/{id} → update an existing sticky note's title/notes/reminder.
export async function updateStickyNote(noteId, { title, targetDate, notes }) {
  return fetchJsonWithAuth(JOBS_URL, `/notes/${encodeURIComponent(noteId)}`, {
    method: 'PUT',
    body: JSON.stringify({
      title,
      notes,
      reminder: targetDate ? { type: 'single', time: targetDate, interval: '' } : null,
      targetDate: targetDate || null,
    }),
  });
}

// PUT /notes/{id}/resolve → mark a sticky note resolved.
export async function resolveStickyNote(noteId) {
  return fetchJsonWithAuth(JOBS_URL, `/notes/${encodeURIComponent(noteId)}/resolve`, {
    method: 'PUT',
  });
}

// ── Tag to Job ────────────────────────────────────────────────────────────────

export async function tagCandidatesToJob({ jobId, candidateIds, hiringStage }) {
  return fetchJsonWithAuth(JOBS_URL, '/source/hiringStgUpdate', {
    method: 'PUT',
    body: JSON.stringify({
      ...getTenantContext(),
      multiJobId: Array.isArray(jobId) ? jobId : [jobId],
      applicantId: Array.isArray(candidateIds) ? candidateIds : [candidateIds],
      updateStatus: String(hiringStage ?? '').toLowerCase(),
      updateStatusType: 'call',
      responseType: 'responded',
      hiriringStatusChangeType: 'tagToJobs',
      comments: '',
      userId: getUserId(),
    }),
  });
}

// ── Add to Groups ─────────────────────────────────────────────────────────────

export async function getCandidateGroups() {
  const json = await fetchJsonWithAuth(
    AUTH_URL,
    '/masters/dropdown?masterName=candidateGroups&offset=0&limit=50',
  );
  return unwrap(json);
}

export async function addCandidatesToGroup({ candidateIds, groupId, groupName }) {
  const json = await fetchJsonWithAuth(CANDIDATES_URL, '/actions/update-group', {
    method: 'PUT',
    body: JSON.stringify({ candidateIds, groupId, groupName }),
  });
  return unwrap(json);
}

// ── Self Assign ───────────────────────────────────────────────────────────────

export async function selfAssignJob({ jobIds, rate, priority }) {
  return fetchJsonWithAuth(JOBS_URL, '/assign?type=self', {
    method: 'PUT',
    body: JSON.stringify({
      multiJobId: Array.isArray(jobIds) ? jobIds : [jobIds],
      userId: getUserId(),
      schemaVersion: 1,
      ...getTenantContext(),
      // jobPayDetails: rate ?? {},
    priority: priority != null ? String(priority) : "false",
      priorityFrom: null,
      recruitersData: [],
    }),
  });
}

// Self-assign confirmation info — the same GET /self-assign the old Jobs
// SelfAssignPopup used to decide between "Are you sure?" and the "Team Members
// Allocated" table. Returns { assignedUsers, totalSubmissions, targetSubmissions,
// message } with the API envelope's `message` preserved (apiGetWithAuth would
// drop it by unwrapping `.data`, so this reads the raw envelope). When a job has
// no submissions the backend returns `data: []` + the confirmation message.
export async function getSelfAssignInfo(jobId) {
  if (!jobId) return { assignedUsers: [], totalSubmissions: 0, targetSubmissions: 0, message: '' };
  const json = await fetchJsonWithAuth(JOBS_URL, `/self-assign?jobId=${encodeURIComponent(jobId)}`);
  const data = json?.data;
  const message = json?.message ?? '';
  if (Array.isArray(data)) {
    const assignedUsers = await Promise.all(data.map(enrichAssignedUser));
    return { assignedUsers, totalSubmissions: 0, targetSubmissions: 0, message };
  }
  if (!data || typeof data !== 'object') {
    return { assignedUsers: [], totalSubmissions: 0, targetSubmissions: 0, message };
  }
  const assignedUsers = await Promise.all(
    (Array.isArray(data.assignedUsers) ? data.assignedUsers : []).map(enrichAssignedUser),
  );
  return {
    assignedUsers,
    totalSubmissions: data.totalSubmissions ?? 0,
    targetSubmissions: data.targetSubmissions ?? 0,
    message,
  };
}

// ── Others Assign ─────────────────────────────────────────────────────────────

export function normalizeAssignableUser(user) {
  const rawId = user?.user_id
    ?? user?.userId
    ?? user?.USER_ID
    ?? user?.legacyUserId
    ?? user?.id
    ?? user?._id;
  const value = rawId === undefined || rawId === null ? '' : String(rawId).trim();
  if (!value) return null;

  const fullName = [user?.first_name ?? user?.firstName, user?.last_name ?? user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const label = user?.name
    || user?.user_name
    || user?.userName
    || user?.USER_NAME
    || fullName
    || user?.email
    || value;

  return { ...user, label: String(label), value };
}

export async function getAssignableUsers() {
  const data = await apiGetWithAuth(JOBS_URL, '/users/manager');
  const users = Array.isArray(data) ? data : (data ? [data] : []);
  return users.map(normalizeAssignableUser).filter(Boolean);
}

// Already-assigned recruiters for the others-assign popup. The old Jobs
// AssignPopup sourced this from GET /pay-details/{id} (job.recruiters), which
// reliably returns the assigned recruiters regardless of submission count —
// unlike /self-assign, whose `assignedUsers` only appears once a job has
// submissions. Normalizes into `recruitersData` [{ recruiterId, id, user_id,
// name }] so both PopupFields (reads user_id/id + name) and
// buildOtherAssignmentPayload (reads recruiterId) work off one shape.
export async function getJobAssignedRecruiters(jobId) {
  if (!jobId) return { recruitersData: [] };
  const data = await apiGetWithAuth(JOBS_URL, `/pay-details/${encodeURIComponent(jobId)}`);
  const recruiters = data && typeof data === 'object' && Array.isArray(data.recruiters)
    ? data.recruiters
    : [];
  const recruitersData = (await Promise.all(recruiters
    .map(async (r) => {
      const id = r?.recruiterId ?? r?.user_id ?? r?.userId ?? r?.id;
      if (id === undefined || id === null || id === '') return null;
      // ALWAYS enrich, even when the row already carries a name. Skipping
      // enrichment for named rows is what left Role and Reporting blank on
      // every "Team Members Allocated" row: the job stores a name but never a
      // role or a reporting manager, so the columns had nothing to render and
      // showed "-". The directory is fetched once and cached, so enriching
      // every row costs the same as enriching one.
      const enriched = await enrichAssignedUser({ ...r, recruiterId: id });
      return {
        recruiterId: id,
        id,
        user_id: id,
        name: enriched.name,
        role_name: enriched.role_name ?? enriched.roleName ?? '',
        reporting_name: enriched.reporting_name ?? enriched.reportingName ?? '',
      };
    })))
    .filter(Boolean);
  return { ...(data && typeof data === 'object' ? data : {}), recruitersData };
}

export function toAssignmentUserId(userId) {
  const numericUserId = Number(userId);
  if (!Number.isInteger(numericUserId) || numericUserId <= 0) {
    throw new Error('A valid user must be selected before assigning the job');
  }
  return numericUserId;
}

function toRecruiterId(recruiter) {
  const rawId = typeof recruiter === 'object'
    ? recruiter?.recruiterId ?? recruiter?.user_id ?? recruiter?.userId ?? recruiter?.id
    : recruiter;
  const id = Number(rawId);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function buildOtherAssignmentPayload({
  jobIds,
  userId,
  rate,
  priority,
  removedIds,
  existingRecruiters,
}) {
  const removed = new Set((removedIds ?? []).map(String));
  const existing = (existingRecruiters ?? [])
    .map(toRecruiterId)
    .filter((id) => id !== null && !removed.has(String(id)));

  // userId may now be an ARRAY (multi-select "Assign to") or a single value.
  // Every selected recruiter is folded into recruitersData — the backend's
  // ResolveAssignmentRecruiterIDs unions userId + recruitersData, so putting them
  // all here assigns everyone at once. userId keeps the first selected for the
  // single-value field / notifications.
  const selected = (Array.isArray(userId) ? userId : [userId])
    .map(toRecruiterId)
    .filter((id) => id !== null);
  // A removal-only submit is legitimate — the user took somebody off the job
  // and added nobody. This used to throw, which (together with the popup's
  // disabled OK button) made removal impossible to save at all. assignJobToOthers
  // only calls this when there IS something to add, so reaching here with an
  // empty selection means a genuinely empty request.
  if (selected.length === 0) {
    throw new Error('A valid user must be selected before assigning the job');
  }

  const recruitersData = Array.from(new Set([...existing, ...selected]));

  return {
    multiJobId: Array.isArray(jobIds) ? jobIds : [jobIds],
    userId: selected[0],
    schemaVersion: 1,
    ...getTenantContext(),
    // jobPayDetails: rate ?? {},
    priority: String(priority ?? false),
    priorityFrom: '',
    recruitersData,
  };
}

// removeJobRecruiters — takes recruiters OFF a job.
//
// The Jobs service's PUT /assign?type=other cannot do this. Proven against the
// live service: it MERGES `recruitersData` into the job's existing recruiters
// (send [14,149,145] for a job holding [14,149,145,74] and 74 survives), and it
// rejects a userId that is already assigned ("Job Already Assigned is
// recruiter"). No payload shape expresses a removal, and that service's source
// is not in this repo.
//
// The Be_Auth gateway CAN: PATCH /module/update/{id} writes the `recruiters`
// array verbatim — a true replace — and the jobs-assign platform workflows
// recompute `assignedUserIds` (assignee + reporting chain) off it afterwards,
// so My Jobs stays correct without any extra call.
//
// The remaining recruiters are taken from the job RECORD, not rebuilt from the
// popup's rows: the popup only carries {recruiterId, name}, so reconstructing
// the array here would silently drop each survivor's email and assignedDateTime.
async function removeJobRecruiters(jobIds, removedIds) {
  const removed = new Set((removedIds ?? []).map(String));
  if (!removed.size) return;

  await Promise.all((jobIds ?? []).map(async (jobId) => {
    const listed = await fetchJsonWithAuth(
      AUTH_URL,
      `/module/list?module=jobs&id=${encodeURIComponent(jobId)}`,
    );
    const payload = listed?.data?.data ?? listed?.data ?? listed;
    const record = Array.isArray(payload) ? payload[0] : payload;
    const current = Array.isArray(record?.recruiters) ? record.recruiters : [];
    const kept = current.filter((r) => !removed.has(String(toRecruiterId(r) ?? '')));
    // Nothing actually matched — don't issue a write that would rewrite the
    // array to itself and stamp a spurious audit entry.
    if (kept.length === current.length) return;
    await fetchJsonWithAuth(AUTH_URL, `/module/update/${encodeURIComponent(jobId)}?module=jobs`, {
      method: 'PATCH',
      body: JSON.stringify({ recruiters: kept }),
    });
  }));
}

export async function assignJobToOthers(params) {
  const jobIds = Array.isArray(params?.jobIds) ? params.jobIds : [params?.jobIds].filter(Boolean);
  const removedIds = params?.removedIds ?? [];
  const selected = (Array.isArray(params?.userId) ? params.userId : [params?.userId])
    .map(toRecruiterId)
    .filter((id) => id !== null);

  // ADDITIONS go through the Jobs service so its side effects (assignment
  // notifications, recruiter records) still happen. Only run it when there is
  // something to add: it errors on an already-assigned user, so a removal-only
  // submit must not touch it.
  let result;
  if (selected.length > 0) {
    result = await fetchJsonWithAuth(JOBS_URL, '/assign?type=other', {
      method: 'PUT',
      body: JSON.stringify(buildOtherAssignmentPayload(params)),
    });
  }

  // REMOVALS afterwards, so the authoritative final list is written last and
  // the add call's merge cannot resurrect somebody the user just took off.
  await removeJobRecruiters(jobIds, removedIds);
  return result;
}

// ── Move to Onboarding ────────────────────────────────────────────────────────

export async function moveToOnboarding({ submissionId, isMail = false }) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/submissions/move-to-onboarding', {
    method: 'POST',
    body: JSON.stringify({ submissionId, isMail: !!isMail }),
  });
  return unwrap(json);
}

// ── Approve Rate ──────────────────────────────────────────────────────────────

export async function approveSubmission(submissionId, payload) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/submissions/approve', {
    method: 'POST',
    body: JSON.stringify({ submissionId, ...payload }),
  });
  return unwrap(json);
}

export async function getVMSApprove(submissionId) {
  const json = await fetchJsonWithAuth(
    AUTH_URL,
    `/submissions/vms-approve?submissionId=${encodeURIComponent(submissionId)}`,
  );
  return unwrap(json);
}

// sendSubmissionForCfoApproval — re-submits a workforce engagement to the CFO
// (POST SUBMISSIONS_URL /workforce-engagements/{id}/cfo-approval/send), the
// same endpoint the Onboarding page's "Send for Approval" uses. Used by the
// submission-manager Re-Approve popup after a CFO rejection: the manager fixes
// the proposed rate and re-sends it for CFO review. billRate = proposed rate,
// payRate = employer/candidate-accepted rate. The backend requires
// proposedRate, candidateRate and a valid approvalUrl.
// `rateUnit` is the unit the recruiter actually chose, resolved by the finance
// engine off the submission (falling back to the job's). It used to be hardcoded
// to 'hr' here, which meant a monthly/yearly engagement was sent to the CFO
// described as hourly — the rate NUMBER was right and its unit was wrong, so the
// request read as a wildly different amount of money. Defaulted to 'hr' so
// existing callers that don't pass it keep their previous behaviour.
export async function sendSubmissionForCfoApproval(engagementId, {
  submissionId = '',
  billRate, payRate, currency = '$', rateUnit = 'hr', referralId, referralRate, referralType, referralEnabled, vmsPercent = 0, documentIds = [], remarks = null,
} = {}) {
  const targetPath = engagementId
    ? `/workforce-engagements/${encodeURIComponent(engagementId)}/cfo-approval/send`
    : `/submissions/${encodeURIComponent(submissionId)}/cfo-approval/send`;
  if (!engagementId && !submissionId) {
    throw new Error('Submission is required to send for approval.');
  }
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const approvalTargetId = engagementId || submissionId;
  const json = await fetchJsonWithAuth(
    SUBMISSIONS_URL,
    targetPath,
    {
      method: 'POST',
      body: JSON.stringify({
        proposedRate: {
          candidateProposedRate: Number(billRate) || 0,
          candidateProposedRateCurrency: currency,
          candidateProposedRateCurrencySymbol: '',
          candidateProposedRateUnit: rateUnit,
        },
        candidateRate: {
          candidateAcceptedRate: Number(payRate) || 0,
          candidateAcceptedCurrency: currency,
          candidateAcceptedCurrencySymbol: '',
          candidateAcceptedPayUnit: rateUnit,
          candidateAcceptedRateNegotiable: false,
        },
        // Omit referralRate during manager re-approval unless the caller
        // intentionally supplies it. Sending 0 would erase the referral
        // entered earlier by the onboarding team.
        ...(referralId !== undefined && referralId !== null && referralId !== ''
          ? { referralId }
          : {}),
        ...(referralRate !== undefined && referralRate !== null
          ? { referralRate: Number(referralRate) || 0 }
          : {}),
        ...(referralType !== undefined && referralType !== null && referralType !== ''
          ? { referralType }
          : {}),
        ...(referralEnabled !== undefined && referralEnabled !== null
          ? { referralEnabled: Boolean(referralEnabled) }
          : {}),
        vmsPercent: Number(vmsPercent) || 0,
        documentIds,
        remarks,
        approvalUrl: `${origin}/cfo-approval/${encodeURIComponent(approvalTargetId)}`,
      }),
    },
  );
  return unwrap(json);
}

// ── Reject ────────────────────────────────────────────────────────────────────

export async function rejectSubmission({ submissionId, rejectReasonId, rejectReason, rejectReasonComments }) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/submissions/reject', {
    method: 'POST',
    body: JSON.stringify({ submissionId, rejectReasonId, rejectReason, rejectReasonComments }),
  });
  return unwrap(json);
}

// Reports whether a candidate already has a (non-deleted) submission for a job,
// so Quick Submit can block a duplicate before starting a fresh submission.
// Returns { exists, submissionId? }.
export async function checkSubmissionExists({ jobId, applicantId }) {
  const params = new URLSearchParams({ jobId: String(jobId ?? ''), applicantId: String(applicantId ?? '') });
  const json = await fetchJsonWithAuth(AUTH_URL, `/submissions/exists?${params.toString()}`);
  return unwrap(json) ?? { exists: false };
}

// Quick Submit dropdown searches, served by the Auth gateway. Unlike the
// Jobs/Candidates microservice list endpoints — which scope results to the
// LOGGED-IN USER (jobs: only where the user is a hiringProgress recruiter;
// candidates: only createdBy the user unless the role has view-all) and so
// return NOTHING for any other user — these are tenant-scoped, so every user
// of the tenant can search its jobs and candidates. Each row also carries
// disable/alreadySubmitted/submissionId so duplicates are flagged in the same
// round trip.
export async function searchQuickSubmitJobs({ searchQuery, candidateId }) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/quick-submit/job-search', {
    method: 'POST',
    body: JSON.stringify({ searchQuery: searchQuery ?? '', candidateId: candidateId ? String(candidateId) : '' }),
  });
  const list = unwrap(json);
  return Array.isArray(list) ? list : [];
}

export async function searchQuickSubmitCandidates({ search, jobId }) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/quick-submit/candidate-search', {
    method: 'POST',
    body: JSON.stringify({ search: search ?? '', jobId: jobId ? String(jobId) : '' }),
  });
  const list = unwrap(json);
  return Array.isArray(list) ? list : [];
}

// Batched form of checkSubmissionExists: given one anchor id and the list of
// ids on the other side, returns which pairs already have a submission. Used to
// mark/disable already-submitted rows in the Quick Submit dropdowns (both
// directions) BEFORE the user picks one.
//
// Pass either { jobId, applicantIds } or { applicantId, jobIds }.
// Returns a Map keyed by the *other* side's id → { submissionId, submittedAt }.
export async function checkSubmissionsExistBulk({ jobId, applicantId, jobIds, applicantIds }) {
  const anchoredOnJob = !!jobId;
  const body = {};
  if (jobId) body.jobId = String(jobId);
  if (applicantId) body.applicantId = String(applicantId);
  if (Array.isArray(jobIds)) body.jobIds = jobIds.filter(Boolean).map(String);
  if (Array.isArray(applicantIds)) body.applicantIds = applicantIds.filter(Boolean).map(String);

  const otherIds = anchoredOnJob ? body.applicantIds : body.jobIds;
  if (!otherIds?.length) return new Map();

  const json = await fetchJsonWithAuth(AUTH_URL, '/submissions/exists-bulk', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const pairs = unwrap(json)?.pairs ?? [];
  return new Map(
    pairs.map((pair) => [
      String(anchoredOnJob ? pair.applicantId : pair.jobId),
      { submissionId: pair.submissionId ?? '', submittedAt: pair.submittedAt ?? null },
    ]),
  );
}

// ── Remove from Shortlist ─────────────────────────────────────────────────────

export async function removeFromShortlist({ jobId, candidateIds }) {
  return fetchJsonWithAuth(JOBS_URL, '/source/hiringStgUpdate', {
    method: 'PUT',
    body: JSON.stringify({
      ...getTenantContext(),
      multiJobId: Array.isArray(jobId) ? jobId : [jobId],
      applicantId: Array.isArray(candidateIds) ? candidateIds : [candidateIds],
      updateStatus: 'pipeline',
      updateStatusType: 'call',
      responseType: 'responded',
      hiriringStatusChangeType: 'tagToJobs',
      comments: '',
      userId: getUserId(),
    }),
  });
}

// ── Document edit requests ────────────────────────────────────────────────────
// Edit-approval workflow for submitted documents (Auth gateway
// /document-request/*). Both endpoints take the FULL selection, so one call
// serves a single row and a bulk selection alike.
//
// The gateway can answer 200 with { status: false } on a business rule, which
// fetchJsonWithAuth does not treat as an error (it only throws on non-2xx) —
// surface it here so the popup shows the reason instead of a success toast.

function throwOnStatusFalse(json) {
  if (json?.status === false) {
    throw new Error(json.error || json.message || 'Request failed. Please try again.');
  }
}

const toIdList = (ids) => (Array.isArray(ids) ? ids : [ids]).filter(Boolean).map(String);

// Raises one edit request per document, all sharing one reason. When none can
// be raised the gateway answers 409, which fetchJsonWithAuth throws.
// Resolves { createdCount, failedCount, createdIds, failures: [{ documentId, error }] }.
export async function createDocumentEditRequests({ module, documentIds, reason }) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/document-request/create', {
    method: 'POST',
    body: JSON.stringify({ module, documentIds: toIdList(documentIds), reason }),
  });
  throwOnStatusFalse(json);
  return unwrap(json);
}

// Rejects pending edit requests with one shared, mandatory reason. Always 200
// from the gateway, even when no id was eligible — callers must read
// rejectedCount. Resolves { rejectedCount, failedCount, rejectedIds, failures: [id] }.
export async function rejectDocumentEditRequests({ ids, rejectionReason }) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/document-request/reject', {
    method: 'POST',
    body: JSON.stringify({ ids: toIdList(ids), rejectionReason }),
  });
  throwOnStatusFalse(json);
  return unwrap(json);
}
